import { useState } from 'react';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { useTranslation } from '~/_shared/utils/hooks';
import { useQuery } from '~/_shared/utils/hooks/useQuery';
import { useUserSign } from '../loginForm/useUserSign';
import { useNavigateAfterLogin } from '../useNavigateAfterLogin';
import { getMsSsoRedirectUri } from './sso.helpers';
import {
  msSsoLogin, SsoLoginResponseCode,
} from './sso.repository';
import { SsoPageComponent } from './ssoPage.component';

export const MS_SECURITY_TOKEN_KEY = 'security_token';
export const MS_SECURITY_TOKEN_ITEM_KEY = 'ms_security_token';
export const MS_SSO_SCOPES = 'openid email profile User.Read';
export const MS_PKCE_CODE_VERIFIER_KEY = 'ms_pkce_code_verifier';

export const MsSsoPageContainer = () => {
  const [t] = useTranslation();
  const query = useQuery();
  const { onLoginSuccess } = useUserSign();

  useNavigateAfterLogin();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const state = query.get('state') || '';
  const code = query.get('code') || '';
  const queryError = query.get('error') || '';

  useOnMount(() => {
    if (queryError) {
      let errorMessage = t('sso.login.error.withCode', { code: '#01' });
      if (queryError === 'access_denied') {
        errorMessage = t('sso.error.permissionsNotGranted');
      }

      setError(errorMessage);
      setIsLoading(false);
      return;
    }

    const performLogin = async () => {
      let errorMessage = undefined;

      const securityToken = new URLSearchParams(JSON.parse(decodeURIComponent(state))).get(MS_SECURITY_TOKEN_KEY) || '';
      const storedSecurityToken = sessionStorage.getItem(MS_SECURITY_TOKEN_ITEM_KEY);

      if (securityToken !== storedSecurityToken || !securityToken || !storedSecurityToken) {
        errorMessage = t('sso.login.error.withCode', { code: '#02' });
      }
      if (storedSecurityToken) {
        sessionStorage.removeItem(MS_SECURITY_TOKEN_ITEM_KEY);
      }

      // Verify PKCE code verifier exists
      const codeVerifier = sessionStorage.getItem(MS_PKCE_CODE_VERIFIER_KEY);
      if (!codeVerifier && !errorMessage) {
        errorMessage = errorMessage = t('sso.login.error.withCode', { code: '#03' });
      }

      if (!errorMessage && codeVerifier) {
        try {
          const loginResult = await msSsoLogin({
            code,
            codeVerifier,
            redirectUri: getMsSsoRedirectUri(),
            state,
          });

          if (loginResult.code === SsoLoginResponseCode.Success) {
            onLoginSuccess(loginResult, { isSsoLogin: true });
          }
        }
        catch (e) {
          errorMessage = errorMessage = t('sso.login.error.withCode', { code: '#04' });
        }
      }

      if (errorMessage) {
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
    };

    performLogin();

    // we want this to happen only once on mount

  });

  return (
    <SsoPageComponent
      error={error}
      isLoading={isLoading}
    />
  );
};
